/*
  File: src/components/Stream/StopStreamModal.jsx
  Purpose: Modal for stopping stream with navigation blocking
  Responsibilities:
  - Show stopping progress UI
  - Block navigation and controls during stop process
  - Redirect to appropriate page after successful stop
*/
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const StopStreamModal = ({ 
    isVisible, 
    isProcessing, 
    onComplete, 
    streamType = 'match' 
}) => {
    // Block navigation during stopping
    useEffect(() => {
        if (isVisible && isProcessing) {
            // Prevent browser back/forward navigation
            const handleBeforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = 'Stream is stopping. Please wait...';
                return 'Stream is stopping. Please wait...';
            };
            
            // Block browser navigation
            window.addEventListener('beforeunload', handleBeforeUnload);
            
            // Block history navigation
            const handlePopState = (e) => {
                if (isProcessing) {
                    // Push current state back to prevent navigation
                    window.history.pushState(null, '', window.location.href);
                }
            };
            
            window.addEventListener('popstate', handlePopState);
            
            // Add initial history state to catch back button
            window.history.pushState(null, '', window.location.href);
            
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [isVisible, isProcessing]);

    // Block page interaction during stopping
    useEffect(() => {
        if (isVisible && isProcessing) {
            // Disable scrolling
            document.body.style.overflow = 'hidden';
            
            // Block all clicks outside modal
            const handleClick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
            };
            
            const handleKeyDown = (e) => {
                // Block all keyboard navigation except allowed keys
                if (e.key === 'Tab' || e.key === 'Escape' || e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                }
            };
            
            document.addEventListener('click', handleClick, true);
            document.addEventListener('keydown', handleKeyDown, true);
            
            return () => {
                document.body.style.overflow = '';
                document.removeEventListener('click', handleClick, true);
                document.removeEventListener('keydown', handleKeyDown, true);
            };
        }
    }, [isVisible, isProcessing]);

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            {/* Backdrop - high z-index to block everything */}
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
            
            {/* Modal */}
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="relative bg-white rounded-xl shadow-2xl p-8 mx-4 max-w-md w-full text-center"
            >
                {isProcessing ? (
                    <>
                        {/* Stopping state */}
                        <div className="mb-6">
                            <div className="w-16 h-16 mx-auto mb-4 relative">
                                <div className="absolute inset-0 border-4 border-red-200 rounded-full"></div>
                                <div className="absolute inset-0 border-4 border-red-500 rounded-full border-t-transparent animate-spin"></div>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                Stopping Stream
                            </h3>
                            <p className="text-gray-600">
                                Please wait while we stop your {streamType} stream.
                                <br />
                                <span className="text-sm text-gray-500 mt-2 block">
                                    This will take just a moment...
                                </span>
                            </p>
                        </div>
                        
                        {/* Progress indicator */}
                        <div className="mb-4">
                            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                                <div className="flex space-x-1">
                                    <motion.div
                                        className="w-2 h-2 bg-red-500 rounded-full"
                                        animate={{
                                            y: [0, -8, 0],
                                        }}
                                        transition={{
                                            duration: 1.2,
                                            repeat: Infinity,
                                            delay: 0,
                                        }}
                                    />
                                    <motion.div
                                        className="w-2 h-2 bg-red-500 rounded-full"
                                        animate={{
                                            y: [0, -8, 0],
                                        }}
                                        transition={{
                                            duration: 1.2,
                                            repeat: Infinity,
                                            delay: 0.2,
                                        }}
                                    />
                                    <motion.div
                                        className="w-2 h-2 bg-red-500 rounded-full"
                                        animate={{
                                            y: [0, -8, 0],
                                        }}
                                        transition={{
                                            duration: 1.2,
                                            repeat: Infinity,
                                            delay: 0.4,
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                        {/* Success state */}
                        <div className="mb-6">
                            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                Stream Stopped
                            </h3>
                            <p className="text-gray-600">
                                Your {streamType} stream has been stopped successfully.
                                <br />
                                <span className="text-sm text-green-600 mt-2 block">
                                    Taking you back to the {streamType === 'series' ? 'TV' : 'Football'} page...
                                </span>
                            </p>
                        </div>
                        
                        {/* Success checkmark animation */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring" }}
                            className="mb-4"
                        >
                            <div className="flex justify-center">
                                <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                                    ✓ Done
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </motion.div>
        </div>
    );
};

export default StopStreamModal;